import { Box, Button, Card, CardContent, CircularProgress, MenuItem, Stack, Tab, Tabs, TextField, Typography } from '@mui/material'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from '../store'
import { API_BASE_URL } from '../config/api'
import dayjs from 'dayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
  useGetSaleContractPagePreferencesQuery,
  usePatchSaleContractListFiltersMutation,
  useSetRepInfoMutation,
} from '../store/api/saleContractApi'
import PageHeader from '../components/PageHeader'
import ChooseRepInfoDialog from './saleContract/ChooseRepInfoDialog'
import ConsignmentListView from './saleContract/ConsignmentListView'
import ContractListView from './saleContract/ContractListView'
import SaleAmendmentListView from './saleContract/SaleAmendmentListView'
import SalePTBFFixationListView from './saleContract/SalePTBFFixationListView'
import SaleReferencesView from './saleContract/SaleReferencesView'

const TAB_CONFIG = [
  { path: 'list', label: 'Sale Contract' },
  { path: 'consignment', label: 'Consignment' },
  { path: 'amendment', label: 'Amendment' },
  { path: 'ptbf-fixation', label: 'PTBF Fixation' },
  { path: 'references', label: 'References' },
] as const

type FinalizedFilter = 'all' | 'yes' | 'no'

const headerButtonSx = {
  minHeight: 24,
  px: 0.75,
  py: 0.15,
  fontSize: '0.67rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

const headerLabelSx = {
  whiteSpace: 'nowrap',
  fontSize: '0.72rem',
  lineHeight: 1.1,
}

const compactInputSx = {
  width: { xs: 130, sm: 148 },
  '& .MuiInputBase-root': {
    minHeight: 26,
    fontSize: '0.72rem',
  },
  '& .MuiInputBase-input': {
    py: 0.25,
    px: 0.75,
  },
}

const compactSelectSx = {
  width: 94,
  '& .MuiInputBase-root': {
    minHeight: 26,
    fontSize: '0.72rem',
  },
  '& .MuiSelect-select': {
    py: 0.25,
    px: 0.75,
  },
}

const getFiscalYearDefaultRange = () => {
  const now = dayjs()
  const startYear = now.month() >= 9 ? now.year() : now.year() - 1

  return {
    startDate: `${startYear}-10-01`,
    endDate: `${startYear + 1}-09-30`,
  }
}

const getDownloadFilename = (disposition: string | null): string | null => {
  if (!disposition) return null

  const filenameStar = /filename\*\s*=\s*([^;]+)/i.exec(disposition)
  if (filenameStar?.[1]) {
    const value = filenameStar[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  const filename = /filename\s*=\s*([^;]+)/i.exec(disposition)
  if (!filename?.[1]) return null
  return filename[1].trim().replace(/^"|"$/g, '')
}

export default function SaleContractPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [repInfoOpen, setRepInfoOpen] = useState(false)
  const [shipmentStart, setShipmentStart] = useState('')
  const [shipmentEnd, setShipmentEnd] = useState('')
  const [finalizedFilter, setFinalizedFilter] = useState<FinalizedFilter>('all')
  const [exporting, setExporting] = useState<'all' | 'filter' | null>(null)
  const prefsHydrated = useRef(false)
  const lastPatchedRef = useRef<{ shipmentPeriodFrom: string; shipmentPeriodTo: string; finalizedFilter: FinalizedFilter } | null>(null)
  const { data: pagePrefs, isSuccess: pagePrefsReady } = useGetSaleContractPagePreferencesQuery()
  const [patchListFilters] = usePatchSaleContractListFiltersMutation()
  const [setRepInfo] = useSetRepInfoMutation()
  const email = useAppSelector((s) => s.auth.user?.email ?? '')
  const exportToken = 'JDHK-NAEB-DQCK-DGFF'

  const downloadSaleContractExcel = async (mode: 'all' | 'filter', url: string) => {
    if (!email) return

    setExporting(mode)
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const filename = getDownloadFilename(res.headers.get('content-disposition'))
      const downloadName =
        mode === 'filter' && filename
          ? filename.replace(/\.xlsx$/i, '_filter.xlsx')
          : filename
      a.href = href
      a.download = downloadName ?? (mode === 'filter' ? 'SaleContract_filter.xlsx' : 'SaleContract.xlsx')
      a.click()
      URL.revokeObjectURL(href)
    } finally {
      setExporting(null)
    }
  }

  const handleExportAll = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/salecontract/data?token=${encodeURIComponent(exportToken)}&email=${encodeURIComponent(email)}`
    await downloadSaleContractExcel('all', url)
  }

  const handleExportFilter = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/salecontract/data_filter?token=${encodeURIComponent(exportToken)}&email=${encodeURIComponent(email)}&start_date=${encodeURIComponent(shipmentStart)}&end_date=${encodeURIComponent(shipmentEnd)}&is_finalized=${encodeURIComponent(finalizedFilter)}`
    await downloadSaleContractExcel('filter', url)
  }

  useEffect(() => {
    if (!pagePrefsReady || !pagePrefs || prefsHydrated.current) return
    prefsHydrated.current = true

    const fiscalYearDefault = getFiscalYearDefaultRange()
    const savedFrom = pagePrefs.shipmentPeriodFrom ?? ''
    const savedTo = pagePrefs.shipmentPeriodTo ?? ''
    const savedFinalized = (pagePrefs.finalizedFilter ?? '') as FinalizedFilter | ''
    const hydratedFrom = savedFrom || fiscalYearDefault.startDate
    const hydratedTo = savedTo || fiscalYearDefault.endDate
    const hydratedFinalized: FinalizedFilter = savedFinalized || 'all'

    // Prevent the debounce PATCH below from firing immediately after hydrate.
    lastPatchedRef.current = {
      shipmentPeriodFrom: hydratedFrom,
      shipmentPeriodTo: hydratedTo,
      finalizedFilter: hydratedFinalized,
    }

    setShipmentStart(hydratedFrom)
    setShipmentEnd(hydratedTo)
    setFinalizedFilter(hydratedFinalized)

    // Persist defaults to backend so GET /sale-contracts applies the same filter.
    const defaultsApplied = !savedFrom || !savedTo || !savedFinalized
    if (defaultsApplied) {
      void patchListFilters({
        shipmentPeriodFrom: hydratedFrom,
        shipmentPeriodTo: hydratedTo,
        finalizedFilter: hydratedFinalized,
      })
    }

    if (pagePrefs.currentSignId == null) {
      void setRepInfo(1)
    }
  }, [pagePrefsReady, pagePrefs, patchListFilters, setRepInfo])

  useEffect(() => {
    if (!prefsHydrated.current) return

    const last = lastPatchedRef.current
    if (last) {
      const unchanged =
        last.shipmentPeriodFrom === shipmentStart && last.shipmentPeriodTo === shipmentEnd && last.finalizedFilter === finalizedFilter
      if (unchanged) return
    }

    const timer = window.setTimeout(() => {
      // Optimistic: treat as patched at debounce time to avoid duplicate PATCH calls.
      lastPatchedRef.current = { shipmentPeriodFrom: shipmentStart, shipmentPeriodTo: shipmentEnd, finalizedFilter }
      void patchListFilters({
        shipmentPeriodFrom: shipmentStart,
        shipmentPeriodTo: shipmentEnd,
        finalizedFilter,
      })
    }, 450)
    return () => window.clearTimeout(timer)
  }, [shipmentStart, shipmentEnd, finalizedFilter, patchListFilters])

  const activeIndex = useMemo(() => {
    const segment = location.pathname.split('/').pop() ?? ''
    const idx = TAB_CONFIG.findIndex((t) => t.path === segment)
    return idx >= 0 ? idx : 0
  }, [location.pathname])

  const current = TAB_CONFIG[activeIndex]
  const hasInvalidShipmentRange =
    Boolean(shipmentStart) && Boolean(shipmentEnd) && shipmentStart > '' && shipmentEnd > '' && shipmentStart > shipmentEnd

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Sale Contract"
        module="Sales"
        actions={
        <Stack
          direction="row"
          spacing={0.45}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
          sx={{ width: '100%' }}
        >
          <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="body2" fontWeight={700} sx={headerLabelSx}>
              Shipment Period:
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                format="DD-MMM-YYYY"
                value={shipmentStart ? dayjs(shipmentStart) : null}
                onChange={(d) => setShipmentStart(d ? d.format('YYYY-MM-DD') : getFiscalYearDefaultRange().startDate)}
                slotProps={{ textField: { size: 'small', sx: compactInputSx, inputProps: { 'aria-label': 'Shipment Period start date' } } }}
              />
              <DatePicker
                format="DD-MMM-YYYY"
                value={shipmentEnd ? dayjs(shipmentEnd) : null}
                onChange={(d) => setShipmentEnd(d ? d.format('YYYY-MM-DD') : getFiscalYearDefaultRange().endDate)}
                slotProps={{ textField: { size: 'small', error: hasInvalidShipmentRange, sx: compactInputSx, inputProps: { 'aria-label': 'Shipment Period end date' } } }}
              />
            </LocalizationProvider>
          </Stack>

          <Stack direction="row" spacing={0.45} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography variant="body2" fontWeight={700} sx={headerLabelSx}>
              Is Finalized:
            </Typography>
            <TextField
              select
              size="small"
              value={finalizedFilter}
              onChange={(event) => setFinalizedFilter(event.target.value as FinalizedFilter)}
              sx={compactSelectSx}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </TextField>
          </Stack>

          <Button size="small" variant="outlined" sx={headerButtonSx} onClick={() => setRepInfoOpen(true)}>
            Choose Rep Info
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={headerButtonSx}
            onClick={handleExportFilter}
            disabled={exporting !== null || !email || hasInvalidShipmentRange}
          >
            {exporting === 'filter' ? <CircularProgress size={11} color="inherit" sx={{ mr: 0.5 }} /> : null}
            Export (Filter)
          </Button>
          <Button
            size="small"
            variant="outlined"
            sx={headerButtonSx}
            onClick={handleExportAll}
            disabled={exporting !== null || !email}
          >
            {exporting === 'all' ? <CircularProgress size={11} color="inherit" sx={{ mr: 0.5 }} /> : null}
            Export (All)
          </Button>
        </Stack>
        }
      />
      {hasInvalidShipmentRange && (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'flex-end', mb: 1 }}>
          Start date must be before or equal to end date
        </Typography>
      )}
      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeIndex}
          onChange={(_, value: number) => {
            navigate(`/sales/contract/${TAB_CONFIG[value].path}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 38,
            '& .MuiTabs-indicator': { height: 2 },
            '& .MuiTab-root': {
              minHeight: 38,
              py: 0.25,
              px: 1.5,
              fontSize: '0.8rem',
              fontWeight: 600,
              textTransform: 'none',
            },
          }}
        >
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.path} label={tab.label} />
          ))}
        </Tabs>
        <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pt: 2 }}>
          {current.path === 'list' ? (
            <ContractListView
              shipmentPeriodStart={shipmentStart}
              shipmentPeriodEnd={shipmentEnd}
              finalizedFilter={finalizedFilter}
            />
          ) : current.path === 'consignment' ? (
            <ConsignmentListView />
          ) : current.path === 'amendment' ? (
            <SaleAmendmentListView />
          ) : current.path === 'ptbf-fixation' ? (
            <SalePTBFFixationListView />
          ) : current.path === 'references' ? (
            <SaleReferencesView />
          ) : (
            <Box sx={{ py: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {TAB_CONFIG[activeIndex]?.label ?? 'Sale Contract'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This screen has not been migrated from Access yet. Use <strong>Sale Contract</strong> for list / New /
                Edit / Print workflows.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
      <ChooseRepInfoDialog open={repInfoOpen} onClose={() => setRepInfoOpen(false)} />
    </Box>
  )
}
